import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Phase 2 — WhatsApp-style badge engine.
 *
 * Maintains a single source of truth for the "unseen" count across:
 *   - public.notifications (is_read = false)
 *   - public.consultation_messages where current user is recipient (is_read = false)
 *
 * Subscribes to realtime changes so the badge stays accurate across devices,
 * and writes the total to the OS app icon via the Badging API where supported.
 */
export function useUnreadBadge(userId: string | null | undefined) {
  const [unread, setUnread] = useState({ notifications: 0, messages: 0 });

  useEffect(() => {
    if (!userId) {
      setUnread({ notifications: 0, messages: 0 });
      // Best-effort clear on sign-out
      // @ts-ignore — Badging API is not in lib.dom yet
      navigator.clearAppBadge?.().catch(() => {});
      return;
    }

    let cancelled = false;
    let debounceTimer: number | undefined;

    const refresh = async () => {
      const { data, error } = await supabase.rpc("get_unread_summary");
      if (cancelled || error || !data || !Array.isArray(data) || !data[0]) return;
      const row = data[0] as { unread_notifications: number; unread_messages: number };
      setUnread({
        notifications: Number(row.unread_notifications) || 0,
        messages: Number(row.unread_messages) || 0,
      });
    };

    // Coalesce bursts of realtime events into a single authoritative recompute.
    const scheduleRefresh = () => {
      if (debounceTimer) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => { void refresh(); }, 400);
    };

    refresh();

    // Re-sync when the tab becomes visible again (catches missed events)
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    // Re-sync immediately on network restore — realtime UPDATEs delivered
    // while offline are gone for good; the only way to fix drift is a recount.
    const onOnline = () => { void refresh(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);

    // Periodic authoritative recompute. Catches:
    //   - dropped realtime UPDATEs on flaky networks
    //   - stale local state after SW updates / multi-tab divergence
    //   - server-side state changes not driven by this user (e.g. another
    //     device marked messages seen).
    const reconcileInterval = window.setInterval(() => { void refresh(); }, 60_000);

    const channel = supabase
      .channel(`unread-badge-${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` },
        () => scheduleRefresh(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "consultation_messages" },
        () => scheduleRefresh(),
      )
      .subscribe((status) => {
        // Realtime (re)connected — recount in case we missed events while down.
        if (status === "SUBSCRIBED") void refresh();
      });

    return () => {
      cancelled = true;
      if (debounceTimer) window.clearTimeout(debounceTimer);
      window.clearInterval(reconcileInterval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // Push the total to the OS app badge whenever it changes.
  useEffect(() => {
    const total = unread.notifications + unread.messages;
    try {
      // @ts-ignore — Badging API is not in lib.dom yet
      if (typeof navigator !== "undefined" && navigator.setAppBadge) {
        if (total > 0) {
          // @ts-ignore
          navigator.setAppBadge(total).catch(() => {});
        } else {
          // @ts-ignore
          navigator.clearAppBadge?.().catch(() => {});
        }
      }
    } catch {
      /* ignore — feature not supported on this platform */
    }
  }, [unread]);

  return {
    ...unread,
    total: unread.notifications + unread.messages,
  };
}
